import { useState, useEffect, useRef } from "react";

const PHASE_COLORS_BG  = ["rgba(232,168,56,0.10)","rgba(192,101,42,0.12)","rgba(123,58,16,0.14)"];
const PHASE_COLORS_BAR = ["#e8a838","#c0652a","#7b3a10"];
const WEATHER_OPTIONS  = ["晴れ","曇り","雨","雪"];
const SCORE_LABELS     = ["酸味","甘み","苦味","ボディ","香り"];
const DEFAULT_TURNING_POINTS = [
  { id:"start",   label:"START", fixed:true,  color:"#2d7a2d" },
  { id:"tp",      label:"TP",    fixed:false, color:"#4a90d9" },
  { id:"power",   label:"火力",  fixed:false, color:"#e53935", isPower:true },
  { id:"dry_end", label:"DE",    fixed:false, color:"#e8a838" },
  { id:"fc",      label:"FC",    fixed:false, color:"#c0652a" },
  { id:"fce",     label:"FCE",   fixed:false, color:"#e07b3a" },
  { id:"sc",      label:"SC",    fixed:false, color:"#9b3fb5" },
  { id:"sce",     label:"SCE",   fixed:false, color:"#b06ac0" },
  { id:"dev_end", label:"STOP",  fixed:true,  color:"#c0392b" },
  { id:"re",      label:"RE",    fixed:false, color:"#c0392b" },
];
const TABLE_TIMES = Array.from({length:21},(_,i)=>i);
const PLAN_ROWS = ["予熱","投入","中点","160°C","180°C","1ハゼ","2ハゼ","排出"];

function fmt(ms){ const s=Math.floor(ms/1000),m=Math.floor(s/60); return `${String(m).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`; }

const Section = ({title, children}) => (
  <div style={{border:"1px solid var(--color-border-secondary)",borderRadius:10,padding:"1rem",marginBottom:16,background:"var(--color-background-primary)"}}>
    {title&&<div style={{fontWeight:600,fontSize:14,marginBottom:12,color:"var(--color-text-primary)"}}>{title}</div>}
    {children}
  </div>
);

function RadarChart({ scores }){
  const labels=["酸味","甘み","苦味","ボディ","香り"];
  const cx=100,cy=100,r=70,ang=i=>(Math.PI*2*i)/labels.length-Math.PI/2;
  const pts=labels.map((_,i)=>({x:cx+r*Math.cos(ang(i)),y:cy+r*Math.sin(ang(i))}));
  const dp=labels.map((l,i)=>{const v=(scores[l]||0)/5;return{x:cx+r*v*Math.cos(ang(i)),y:cy+r*v*Math.sin(ang(i))};});
  const gp=ratio=>pts.map(p=>`${cx+(p.x-cx)*ratio},${cy+(p.y-cy)*ratio}`).join(" ");
  return(
    <svg viewBox="0 0 200 200" width="180" height="180">
      {[.25,.5,.75,1].map(rv=><polygon key={rv} points={gp(rv)} fill="none" stroke="var(--color-border-tertiary)" strokeWidth=".5"/>)}
      {pts.map((p,i)=><line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="var(--color-border-tertiary)" strokeWidth=".5"/>)}
      <polygon points={dp.map(p=>`${p.x},${p.y}`).join(" ")} fill="rgba(186,117,23,.25)" stroke="#BA7517" strokeWidth="1.5"/>
      {labels.map((l,i)=>{
        const lx=cx+(r+14)*Math.cos(ang(i)),ly=cy+(r+14)*Math.sin(ang(i));
        return <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="var(--color-text-secondary)">{l}</text>;
      })}
    </svg>
  );
}

function RoastChart({ points, setPoints, turningPoints, customTPs, powerPoints, readOnly }){
  const W=680,H=300,PAD={l:50,r:54,t:16,b:32};
  const MIN_T=50,MAX_T=260,MIN_ROR=0,MAX_ROR=20;
  const draggingRef=useRef(null);
  const svgRef=useRef(null);
  const [tooltip,setTooltip]=useState(null);
  const [hoverTP,setHoverTP]=useState(null);
  const [cursorInfo,setCursorInfo]=useState(null);

  const safe=Array.isArray(points)?points:[];
  const sorted=[...safe].sort((a,b)=>a.time-b.time);
  const maxT=Math.max(14,sorted.length>0?Math.ceil(sorted[sorted.length-1].time)+2:14);

  const sx =t   =>PAD.l+(t/maxT)*(W-PAD.l-PAD.r);
  const sy =temp=>PAD.t+(1-(temp-MIN_T)/(MAX_T-MIN_T))*(H-PAD.t-PAD.b);
  const syr=ror =>PAD.t+(1-(ror-MIN_ROR)/(MAX_ROR-MIN_ROR))*(H-PAD.t-PAD.b);
  const fromSVG=(svgX,svgY)=>({
    time:Math.round(((svgX-PAD.l)/(W-PAD.l-PAD.r))*maxT*10)/10,
    temp:Math.round(MIN_T+(1-(svgY-PAD.t)/(H-PAD.t-PAD.b))*(MAX_T-MIN_T)),
  });
  const getSVGCoords=e=>{
    const svg=svgRef.current;if(!svg)return null;
    const rect=svg.getBoundingClientRect();
    const cx=e.touches?e.touches[0].clientX:e.clientX;
    const cy=e.touches?e.touches[0].clientY:e.clientY;
    return{x:((cx-rect.left)/rect.width)*W,y:((cy-rect.top)/rect.height)*H};
  };
  const handleClick=e=>{
    return;
  };
  const handlePtDown=(e,id)=>{
    if(readOnly)return;
    e.stopPropagation();e.preventDefault();draggingRef.current=id;
  };
  useEffect(()=>{
    const onMove=e=>{
      if(draggingRef.current===null)return;
      const c=getSVGCoords(e);if(!c)return;
      const{time,temp}=fromSVG(c.x,c.y);
      setPoints(prev=>(Array.isArray(prev)?prev:[]).map(p=>
        p.id===draggingRef.current?{...p,time:Math.max(0,time),temp:Math.min(MAX_T,Math.max(MIN_T,temp))}:p
      ));
    };
    const onUp=()=>{draggingRef.current=null;};
    window.addEventListener("mousemove",onMove);window.addEventListener("mouseup",onUp);
    window.addEventListener("touchmove",onMove,{passive:false});window.addEventListener("touchend",onUp);
    return()=>{window.removeEventListener("mousemove",onMove);window.removeEventListener("mouseup",onUp);window.removeEventListener("touchmove",onMove);window.removeEventListener("touchend",onUp);};
  },[maxT]);

  const rorPts=sorted.length>1?sorted.slice(1).map((p,i)=>{
    const prev=sorted[i],dt=p.time-prev.time;
    const ror=dt>0?(p.temp-prev.temp)/dt:0;
    return{time:(p.time+prev.time)/2,ror:Math.max(MIN_ROR,Math.min(MAX_ROR,ror))};
  }):[];
  const tempPath=sorted.length>1?sorted.map((p,i)=>`${i===0?"M":"L"}${sx(p.time).toFixed(1)} ${sy(p.temp).toFixed(1)}`).join(" "):"";
  const rorPath=rorPts.length>1?rorPts.map((p,i)=>`${i===0?"M":"L"}${sx(p.time).toFixed(1)} ${syr(p.ror).toFixed(1)}`).join(" "):"";

  const allTPs=[...(Array.isArray(turningPoints)?turningPoints:[]),...(Array.isArray(customTPs)?customTPs:[])];
  const getMs=id=>allTPs.find(t=>t.id===id)?.elapsed;
  const toMin=ms=>ms!=null?ms/1000/60:null;
  const startMin=toMin(getMs("start"))??0;
  const dryMin=toMin(getMs("dry_end")),fcMin=toMin(getMs("fc")),devMin=toMin(getMs("dev_end"));
  const phases=[{from:startMin,to:dryMin,ci:0},{from:dryMin,to:fcMin,ci:1},{from:fcMin,to:devMin,ci:2}].filter(p=>p.from!=null&&p.to!=null);
  const markers=allTPs.filter(tp=>tp.elapsed!=null&&!tp.isPower);
  const yTL=[60,100,140,180,220,260],yRL=[0,5,10,15,20,25,30];
  const xL=Array.from({length:Math.ceil(maxT)+1},(_,i)=>i).filter(v=>v%2===0&&v<=maxT);

  const safePowerPoints=Array.isArray(powerPoints)?powerPoints:[];

  return(
    <div style={{position:"relative"}}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%"
        style={{display:"block",border:"0.5px solid var(--color-border-tertiary)",borderRadius:8,userSelect:"none",cursor:readOnly?"default":"crosshair",touchAction:"none"}}
        onClick={handleClick}
        onMouseMove={e=>{
          if(readOnly)return;
          const c=getSVGCoords(e);if(!c)return;
          const{time,temp}=fromSVG(c.x,c.y);
          if(time>=0&&temp>=MIN_T&&temp<=MAX_T)setCursorInfo({px:e.clientX,py:e.clientY,time,temp});
          else setCursorInfo(null);
        }}
        onMouseLeave={()=>setCursorInfo(null)}>
        {phases.map((ph,i)=>(
          <rect key={i} x={sx(ph.from)} y={PAD.t} width={sx(ph.to)-sx(ph.from)} height={H-PAD.t-PAD.b} fill={PHASE_COLORS_BG[ph.ci]}/>
        ))}
        {yTL.map(v=>{const y=sy(v);return(<g key={v}><line x1={PAD.l} y1={y} x2={W-PAD.r} y2={y} stroke="var(--color-border-tertiary)" strokeWidth=".5" strokeDasharray="3,3"/><text x={PAD.l-4} y={y} textAnchor="end" dominantBaseline="middle" fontSize="9" fill="var(--color-text-tertiary)">{v}</text></g>);})}
        {yRL.map(v=><text key={v} x={W-PAD.r+4} y={syr(v)} textAnchor="start" dominantBaseline="middle" fontSize="9" fill="#3B6D11">{v}</text>)}
        {xL.map(v=>(<g key={v}><line x1={sx(v)} y1={PAD.t} x2={sx(v)} y2={H-PAD.b} stroke="var(--color-border-tertiary)" strokeWidth=".5" strokeDasharray="2,4"/><text x={sx(v)} y={H-PAD.b+10} textAnchor="middle" fontSize="9" fill="var(--color-text-tertiary)">{v}m</text></g>))}
        {markers.map(tp=>{
          const min=tp.elapsed/1000/60,x=sx(min);
          const nearest=sorted.length>0?sorted.reduce((best,p)=>Math.abs(p.time-min)<Math.abs((best?.time??999)-min)?p:best,null):null;
          const isHovered=hoverTP===tp.id;
          const tpColor=tp.color||"#888";
          return(
            <g key={tp.id}>
              <line x1={x} y1={PAD.t} x2={x} y2={H-PAD.b} stroke={isHovered?tpColor:"#bbb"} strokeWidth={isHovered?2:1} strokeDasharray="4,3"/>
              <line x1={x} y1={PAD.t} x2={x} y2={H-PAD.b} stroke="transparent" strokeWidth="20" style={{cursor:"pointer"}}
                onMouseEnter={e=>{
                  setHoverTP(tp.id);
                  const rect=svgRef.current.getBoundingClientRect();
                  const px=((x/W)*rect.width)+rect.left;
                  const py=nearest?((sy(nearest.temp)/H)*rect.height)+rect.top:rect.top+40;
                  setTooltip({px,py,label:tp.label,temp:nearest?.temp,time:min,color:tpColor});
                }}
                onMouseLeave={()=>{setHoverTP(null);setTooltip(null);}}/>
            </g>
          );
        })}
        {/* 火力ポイント - グラフ上部に配置、色分け */}
        {safePowerPoints.map((pp,idx)=>{
          const min=pp.elapsed/1000/60,x=sx(min);
          const prevVal=idx>0?safePowerPoints[idx-1].value:pp.value;
          const color=idx===0?"#e53935":pp.value>prevVal?"#e53935":"#1565c0";
          return(
            <g key={pp.id}>
              <circle cx={x} cy={PAD.t+14} r="11" fill={color}/>
              <text x={x} y={PAD.t+14} textAnchor="middle" dominantBaseline="middle" fontSize="9" fontWeight="700" fill="white">{pp.value}</text>
            </g>
          );
        })}
        {rorPath&&<path d={rorPath} fill="none" stroke="#639922" strokeWidth="1.5" strokeDasharray="5,3"/>}
        {tempPath&&<path d={tempPath} fill="none" stroke="#BA7517" strokeWidth="2.5" strokeLinejoin="round"/>}
        {!readOnly&&sorted.map(p=>(
          <g key={p.id}>
            <circle cx={sx(p.time)} cy={sy(p.temp)} r="12" fill="transparent" style={{cursor:"grab",touchAction:"none"}} onMouseDown={e=>handlePtDown(e,p.id)} onTouchStart={e=>handlePtDown(e,p.id)} onClick={e=>e.stopPropagation()}/>
            <circle cx={sx(p.time)} cy={sy(p.temp)} r="5" fill="#BA7517" stroke="white" strokeWidth="1.5" style={{pointerEvents:"none"}}/>
          </g>
        ))}
        {readOnly&&sorted.map(p=>(
          <circle key={p.id} cx={sx(p.time)} cy={sy(p.temp)} r="3.5" fill="#BA7517" stroke="white" strokeWidth="1.2"/>
        ))}
      </svg>
      {tooltip&&(
        <div style={{position:"fixed",left:tooltip.px+10,top:tooltip.py-40,background:"var(--color-background-primary)",border:`1px solid ${tooltip.color}`,borderRadius:6,padding:"5px 10px",fontSize:13,pointerEvents:"none",zIndex:100,whiteSpace:"nowrap",boxShadow:"0 2px 8px rgba(0,0,0,.15)"}}>
          <span style={{fontWeight:700,color:tooltip.color}}>{tooltip.label}</span>
          {tooltip.temp!=null&&<span style={{marginLeft:8,color:"#BA7517"}}>{tooltip.temp}°C</span>}
          <span style={{marginLeft:8,color:"var(--color-text-secondary)"}}>{tooltip.time.toFixed(2)}m</span>
        </div>
      )}
      {cursorInfo&&(
        <div style={{position:"fixed",left:cursorInfo.px+14,top:cursorInfo.py-40,background:"var(--color-background-primary)",border:"0.5px solid var(--color-border-tertiary)",borderRadius:6,padding:"4px 10px",fontSize:13,pointerEvents:"none",zIndex:101,whiteSpace:"nowrap",boxShadow:"0 2px 8px rgba(0,0,0,.12)"}}>
          <span style={{color:"#BA7517",fontWeight:600}}>{cursorInfo.temp}°C</span>
          <span style={{margin:"0 4px",color:"var(--color-text-tertiary)"}}>/</span>
          <span style={{color:"var(--color-text-secondary)"}}>{cursorInfo.time.toFixed(1)}m</span>
        </div>
      )}
    </div>
  );
}

function TempTableInput({ points, setPoints, stopElapsed, tpElapsed, fcElapsed, scElapsed }){
  const safe=Array.isArray(points)?points:[];
  const stopMin=stopElapsed!=null?Math.ceil(stopElapsed/1000/60):20;
  const maxMin=Math.min(Math.max(stopMin,1),20);
  const times=Array.from({length:maxMin+1},(_,i)=>i);
  const stopSec=stopElapsed!=null?Math.floor((stopElapsed/1000)%60):null;
  const hasPartialStop=stopElapsed!=null&&stopSec!=null&&stopSec>0;
  const stopTimeLabel=stopElapsed!=null?`${Math.floor(stopElapsed/1000/60)}:${String(Math.floor((stopElapsed/1000)%60)).padStart(2,"0")}`:null;
  const getTemp=t=>{const p=safe.find(p=>Math.abs(p.time-t)<0.01);return p?String(p.temp):"";};
  const getTempStop=()=>{if(stopElapsed==null)return"";const t=stopElapsed/1000/60;const p=safe.find(p=>Math.abs(p.time-t)<0.05);return p?String(p.temp):"";};
  const getTempAt=(elapsed,id)=>{if(elapsed==null)return"";const t=elapsed/1000/60;const p=safe.find(p=>Math.abs(p.time-t)<0.05);return p?String(p.temp):"";};
  const handleChange=(t,val)=>{
    const num=parseInt(val);
    setPoints(prev=>{
      const arr=Array.isArray(prev)?prev.filter(p=>Math.abs(p.time-t)>=0.01):[];
      if(!isNaN(num)&&val.trim()!=="")return[...arr,{id:`tbl-${t}`,time:t,temp:num}];
      return arr;
    });
  };
  const handleChangeStop=(val)=>{
    if(stopElapsed==null)return;
    const t=stopElapsed/1000/60;
    const num=parseInt(val);
    setPoints(prev=>{
      const arr=Array.isArray(prev)?prev.filter(p=>Math.abs(p.time-t)>=0.05):[];
      if(!isNaN(num)&&val.trim()!=="")return[...arr,{id:"tbl-stop",time:t,temp:num}];
      return arr;
    });
  };
  const handleChangeAt=(elapsed,id,val)=>{
    if(elapsed==null)return;
    const t=elapsed/1000/60;
    const num=parseInt(val);
    setPoints(prev=>{
      const arr=Array.isArray(prev)?prev.filter(p=>Math.abs(p.time-t)>=0.05):[];
      if(!isNaN(num)&&val.trim()!=="")return[...arr,{id,time:t,temp:num}];
      return arr;
    });
  };
  const row1=times.slice(0,Math.min(11,times.length));
  const row2=times.slice(11);
  const cellStyle={display:"flex",flexDirection:"column",alignItems:"center",gap:0,minWidth:46,flexShrink:0};
  const headerStyle={fontSize:10,color:"var(--color-text-tertiary)",fontWeight:600,background:"var(--color-background-secondary)",width:"100%",textAlign:"center",padding:"3px 0",borderRadius:"4px 4px 0 0",border:"1px solid var(--color-border-tertiary)",borderBottom:"none"};
  const inputStyle={width:46,fontSize:13,textAlign:"center",padding:"4px 2px",border:"1px solid var(--color-border-tertiary)",borderRadius:"0 0 4px 4px",background:"var(--color-background-primary)",color:"var(--color-text-primary)"};
  const renderRow=ts=>(
    <div style={{display:"flex",gap:4,overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:4}}>
      {ts.map(t=>(
        <div key={t} style={cellStyle}>
          <div style={headerStyle}>{t}m</div>
          <input type="number" min={50} max={260} inputMode="numeric" value={getTemp(t)} onChange={e=>handleChange(t,e.target.value)} placeholder="—" style={inputStyle}/>
        </div>
      ))}
    </div>
  );
  const specialPoints=[
    {elapsed:tpElapsed,id:"tbl-tp",label:"中点",color:"#4a90d9"},
    {elapsed:fcElapsed,id:"tbl-fc",label:"1ハゼ",color:"#c0652a"},
    {elapsed:scElapsed,id:"tbl-sc",label:"2ハゼ",color:"#9b3fb5"},
    ...(hasPartialStop?[{elapsed:stopElapsed,id:"tbl-stop",label:"焙煎終了",color:"#BA7517",isStop:true}]:[]),
  ].filter(sp=>sp.elapsed!=null&&Math.floor((sp.elapsed/1000)%60)>0||sp.elapsed!=null&&sp.label!=="焙煎終了");
  const allSpecial=[
    {elapsed:tpElapsed,id:"tbl-tp",label:"中点",color:"#4a90d9"},
    {elapsed:fcElapsed,id:"tbl-fc",label:"1ハゼ",color:"#c0652a"},
    {elapsed:scElapsed,id:"tbl-sc",label:"2ハゼ",color:"#9b3fb5"},
    ...(hasPartialStop?[{elapsed:stopElapsed,id:"tbl-stop",label:"焙煎終了",color:"#BA7517",isStop:true}]:[]),
  ].filter(sp=>sp.elapsed!=null);
  return(
    <div style={{marginTop:10}}>
      <div style={{fontSize:12,color:"var(--color-text-secondary)",marginBottom:8}}>各分の温度を入力（°C）</div>
      {renderRow(row1)}
      {row2.length>0&&<div style={{marginTop:6}}>{renderRow(row2)}</div>}
      {allSpecial.length>0&&(
        <div style={{marginTop:10}}>
          <div style={{fontSize:12,color:"var(--color-text-secondary)",marginBottom:6}}>特定ポイントの温度</div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {allSpecial.map(sp=>{
              const timeLabel=`${Math.floor(sp.elapsed/1000/60)}:${String(Math.floor((sp.elapsed/1000)%60)).padStart(2,"0")}`;
              const val=sp.isStop?getTempStop():getTempAt(sp.elapsed,sp.id);
              return(
                <div key={sp.id} style={cellStyle}>
                  <div style={{...headerStyle,background:`rgba(${sp.color==="#4a90d9"?"74,144,217":sp.color==="#c0652a"?"192,101,42":sp.color==="#9b3fb5"?"155,63,181":"186,117,23"},.15)`,color:sp.color,fontWeight:700,fontSize:9}}>{sp.label}<br/>{timeLabel}</div>
                  <input type="number" min={50} max={260} inputMode="numeric"
                    value={val}
                    onChange={e=>sp.isStop?handleChangeStop(e.target.value):handleChangeAt(sp.elapsed,sp.id,e.target.value)}
                    placeholder="—" style={{...inputStyle,border:`1px solid ${sp.color}`}}/>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function RoastPlanTable({ plan, setPlan }){
  const cols=["温度","時間","火力","備考"];
  const updateCell=(row,col,val)=>{
    setPlan(prev=>{
      const next={...prev};
      if(!next[row])next[row]={};
      next[row]={...next[row],[col]:val};
      return next;
    });
  };
  const thStyle={fontSize:12,fontWeight:600,color:"var(--color-text-secondary)",padding:"6px 4px",borderBottom:"1px solid var(--color-border-secondary)",borderRight:"1px solid var(--color-border-tertiary)",textAlign:"center",background:"var(--color-background-secondary)",whiteSpace:"nowrap"};
  const tdLabelStyle={fontSize:13,fontWeight:600,padding:"6px 8px",borderBottom:"1px solid var(--color-border-tertiary)",borderRight:"1px solid var(--color-border-tertiary)",background:"var(--color-background-secondary)",whiteSpace:"nowrap",textAlign:"center"};
  const tdStyle={padding:"3px 4px",borderBottom:"1px solid var(--color-border-tertiary)",borderRight:"1px solid var(--color-border-tertiary)"};
  return(
    <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
      <table style={{borderCollapse:"collapse",width:"100%",border:"1px solid var(--color-border-secondary)",borderRadius:8,overflow:"hidden"}}>
        <thead>
          <tr>
            <th style={{...thStyle,minWidth:60}}>TP</th>
            {cols.map(c=><th key={c} style={{...thStyle,minWidth:c==="備考"?120:70}}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {PLAN_ROWS.map(row=>(
            <tr key={row}>
              <td style={tdLabelStyle}>{row}</td>
              {cols.map(col=>(
                <td key={col} style={tdStyle}>
                  <input
                    type={col==="備考"?"text":"text"}
                    inputMode={col==="火力"||col==="温度"?"numeric":undefined}
                    value={plan?.[row]?.[col]||""}
                    onChange={e=>updateCell(row,col,e.target.value)}
                    style={{width:"100%",fontSize:13,border:"none",background:"transparent",color:"var(--color-text-primary)",padding:"4px 2px",outline:"none"}}
                    placeholder="—"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const emptyForm=()=>({
  date:new Date().toISOString().slice(0,16),beanName:"",origin:"",batch:1,
  inputWeight:"",outputWeight:"",weather:"晴れ",temperature:"",humidity:"",memo:"",
  memoImages:[],
  scores:{酸味:3,甘み:3,苦味:3,ボディ:3,香り:3,総合:3},
  tempPoints:[],
  turningPoints:DEFAULT_TURNING_POINTS.map(tp=>({...tp,elapsed:null})),
  customTPs:[],
  powerPoints:[],
  roastPlan:{},
});

export default function App(){
  const [view,setView]=useState("list");
  const [profiles,setProfiles]=useState(()=>{
    try{ return JSON.parse(localStorage.getItem("roast_profiles")||"[]"); }
    catch{ return []; }
  });
  const [form,setFormRaw]=useState(emptyForm());
  const [editId,setEditId]=useState(null);
  const [timerRunning,setTimerRunning]=useState(false);
  const [timerStart,setTimerStart]=useState(null);
  const [elapsed,setElapsed]=useState(0);
  const [newTPLabel,setNewTPLabel]=useState("");
  const [powerInput,setPowerInput]=useState(5);
  const [showPowerInput,setShowPowerInput]=useState(false);
  const [lightboxImg,setLightboxImg]=useState(null);
  const [showChartLightbox,setShowChartLightbox]=useState(false);
  const [formHistory,setFormHistory]=useState([]);
  const [autoSaveMsg,setAutoSaveMsg]=useState("");
  const timerRef=useRef(null);
  const imageInputRef=useRef(null);
  const autoSaveTimerRef=useRef(null);
  const isFirstRender=useRef(true);

  const setForm=updater=>{
    setFormRaw(prev=>{
      const next=typeof updater==="function"?updater(prev):updater;
      setFormHistory(h=>[...h.slice(-19),prev]);
      return next;
    });
  };
  const undoForm=()=>{
    setFormHistory(h=>{
      if(h.length===0)return h;
      const prev=h[h.length-1];
      setFormRaw(prev);
      return h.slice(0,-1);
    });
  };

  useEffect(()=>{
    try{ localStorage.setItem("roast_profiles",JSON.stringify(profiles)); }
    catch{}
  },[profiles]);

  // 自動保存: editId がある編集中のみ、form変更から1秒後に保存
  useEffect(()=>{
    if(isFirstRender.current){isFirstRender.current=false;return;}
    if(!editId)return;
    if(autoSaveTimerRef.current)clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current=setTimeout(()=>{
      setProfiles(prev=>prev.map(p=>p.id===editId?{...form,id:editId}:p));
      setAutoSaveMsg("自動保存しました");
      setTimeout(()=>setAutoSaveMsg(""),2000);
    },1000);
    return()=>{if(autoSaveTimerRef.current)clearTimeout(autoSaveTimerRef.current);};
  },[form]);

  const lastMinRef=useRef(0);
  const audioCtxRef=useRef(null);
  const initAudio=()=>{try{if(!audioCtxRef.current){audioCtxRef.current=new(window.AudioContext||window.webkitAudioContext)();}if(audioCtxRef.current.state==="suspended"){audioCtxRef.current.resume();}}catch(e){}};
  const playBeep=()=>{try{const c=audioCtxRef.current;if(!c)return;const o=c.createOscillator();const g=c.createGain();o.connect(g);g.connect(c.destination);o.frequency.value=880;o.type="sine";g.gain.setValueAtTime(0.4,c.currentTime);g.gain.exponentialRampToValueAtTime(0.001,c.currentTime+0.5);o.start(c.currentTime);o.stop(c.currentTime+0.5);}catch(e){}};
  useEffect(()=>{
    if(timerRunning){
      timerRef.current=setInterval(()=>{
        const ms=Date.now()-timerStart;
        setElapsed(ms);
        const cm=Math.floor(ms/1000/60);
        if(cm>lastMinRef.current){lastMinRef.current=cm;playBeep();}
      },100);
    } else {clearInterval(timerRef.current);lastMinRef.current=0;}
    return()=>clearInterval(timerRef.current);
  },[timerRunning,timerStart]);

  const startTimer=()=>{
    initAudio();
    const now=Date.now();setTimerStart(now);setElapsed(0);setTimerRunning(true);
    setForm(f=>({...f,turningPoints:f.turningPoints.map(tp=>tp.id==="start"?{...tp,elapsed:0}:tp)}));
  };
  const stopTimer=()=>{
    const ms=elapsed;setTimerRunning(false);
    setFormRaw(f=>{
      const updated={...f,turningPoints:f.turningPoints.map(tp=>tp.id==="dev_end"?{...tp,elapsed:ms}:tp)};
      const profile={...updated,id:Date.now(),yield:yield_,phasePcts:{a:aPct,b:bPct,c:cPct}};
      setProfiles(prev=>[...prev,profile]);
      return emptyForm();
    });
    setElapsed(0);
    setFormHistory([]);
    setView("list");
  };
  const recordTP=id=>{
    const ms=elapsed;
    setForm(f=>({...f,turningPoints:f.turningPoints.map(tp=>{
      if(tp.id===id)return{...tp,elapsed:ms};
      if(id==="re"&&tp.id==="dev_end")return{...tp,elapsed:ms};
      return tp;
    })}));
  };
  const recordCTP=id=>{const ms=elapsed;setForm(f=>({...f,customTPs:f.customTPs.map(tp=>tp.id===id?{...tp,elapsed:ms}:tp)}));};
  const recordPower=()=>{
    const ms=elapsed;
    setForm(f=>({...f,powerPoints:[...(f.powerPoints||[]),{id:Date.now(),elapsed:ms,value:powerInput}]}));
    setShowPowerInput(false);
  };
  const addCTP=()=>{
    if(!newTPLabel.trim())return;
    setForm(f=>({...f,customTPs:[...f.customTPs,{id:Date.now(),label:newTPLabel.trim(),elapsed:null,color:"#607d8b"}]}));
    setNewTPLabel("");
  };

  const handleImageUpload=e=>{
    const files=Array.from(e.target.files);
    files.forEach(file=>{
      const reader=new FileReader();
      reader.onload=ev=>{
        setForm(f=>({...f,memoImages:[...(f.memoImages||[]),{id:Date.now()+Math.random(),src:ev.target.result,name:file.name}]}));
      };
      reader.readAsDataURL(file);
    });
    e.target.value="";
  };
  const removeImage=id=>setForm(f=>({...f,memoImages:(f.memoImages||[]).filter(img=>img.id!==id)}));

  const allTPs=[...form.turningPoints,...form.customTPs];
  const getMs=id=>allTPs.find(t=>t.id===id)?.elapsed;
  const startMs=getMs("start")??0;
  const dryEnd=getMs("dry_end"),fcEnd=getMs("fc"),devEnd=getMs("dev_end");
  const totalTime=devEnd!=null?devEnd-startMs:null;
  const aTime=dryEnd!=null?dryEnd-startMs:null;
  const bTime=dryEnd!=null&&fcEnd!=null?fcEnd-dryEnd:null;
  const cTime=fcEnd!=null&&devEnd!=null?devEnd-fcEnd:null;
  const aPct=totalTime&&aTime?Math.round(aTime/totalTime*100):null;
  const bPct=totalTime&&bTime?Math.round(bTime/totalTime*100):null;
  const cPct=totalTime&&cTime?Math.round(cTime/totalTime*100):null;
  const yield_=form.inputWeight&&form.outputWeight?((parseFloat(form.outputWeight)/parseFloat(form.inputWeight))*100).toFixed(1):null;
  const setTempPoints=pts=>setForm(f=>({...f,tempPoints:typeof pts==="function"?pts(f.tempPoints):pts}));

  const saveProfile=()=>{
    const profile={...form,id:editId||Date.now(),yield:yield_,phasePcts:{a:aPct,b:bPct,c:cPct}};
    setProfiles(prev=>editId?prev.map(p=>p.id===editId?profile:p):[...prev,profile]);
    setFormRaw(emptyForm());setEditId(null);setTimerRunning(false);setElapsed(0);setFormHistory([]);setView("list");
  };
  const editProfile=p=>{setFormRaw({...p,memoImages:p.memoImages||[],powerPoints:p.powerPoints||[],roastPlan:p.roastPlan||{}});setEditId(p.id);setFormHistory([]);setView("new");};
  const deleteProfile=id=>setProfiles(prev=>prev.filter(p=>p.id!==id));

  if(view==="list"){
    return(
      <div style={{padding:"1rem"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1rem"}}>
          <h2 style={{fontSize:20,fontWeight:700,margin:0}}>Roast Profiles</h2>
        </div>
        <button onClick={()=>{setForm(emptyForm());setEditId(null);setView("new");}}
          style={{width:"100%",marginBottom:"1rem",padding:"12px",background:"#BA7517",color:"#fff",border:"none",borderRadius:10,fontSize:16,fontWeight:600,cursor:"pointer"}}>
          ＋ 新規プロファイル
        </button>
        {profiles.length===0?(
          <div style={{color:"var(--color-text-secondary)",fontSize:14,padding:"3rem 0",textAlign:"center"}}>まだプロファイルがありません。<br/>最初の焙煎を記録しましょう！</div>
        ):[...profiles].reverse().map(p=>(
          <div key={p.id} style={{background:"var(--color-background-primary)",border:"1px solid var(--color-border-secondary)",borderRadius:10,padding:"1rem",marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:16}}>{p.beanName||"（豆名未入力）"}</div>
                <div style={{fontSize:12,color:"var(--color-text-secondary)",marginTop:3}}>{new Date(p.date).toLocaleString("ja-JP")}</div>
                <div style={{fontSize:12,color:"var(--color-text-secondary)"}}>{p.origin||"産地不明"} ・ バッチ{p.batch}</div>
                <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                  {p.inputWeight&&<span style={{fontSize:11,background:"var(--color-background-secondary)",borderRadius:6,padding:"2px 7px"}}>投入{p.inputWeight}g→{p.outputWeight}g {p.yield?`(${p.yield}%)`:""}</span>}
                  {p.phasePcts?.a!=null&&<span style={{fontSize:11,background:"var(--color-background-secondary)",borderRadius:6,padding:"2px 7px"}}>A:{p.phasePcts.a}% B:{p.phasePcts.b}% C:{p.phasePcts.c}%</span>}
                  {p.weather&&<span style={{fontSize:11,background:"var(--color-background-secondary)",borderRadius:6,padding:"2px 7px"}}>{p.weather} {p.temperature}°C {p.humidity}%</span>}
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6,marginLeft:10,flexShrink:0}}>
                <button onClick={()=>editProfile(p)} style={{fontSize:13,padding:"6px 14px"}}>編集</button>
                <button onClick={()=>deleteProfile(p.id)} style={{fontSize:13,padding:"6px 14px",color:"var(--color-text-danger)"}}>削除</button>
              </div>
            </div>
            {Array.isArray(p.tempPoints)&&p.tempPoints.length>1&&(
              <div style={{marginTop:12}}>
                <RoastChart points={p.tempPoints} setPoints={()=>{}} turningPoints={p.turningPoints||[]} customTPs={p.customTPs||[]} powerPoints={p.powerPoints||[]} readOnly={true}/>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return(
    <div style={{padding:"1rem"}}>
      {/* ライトボックス */}
      {lightboxImg&&(
        <div onClick={()=>setLightboxImg(null)} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.85)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <img src={lightboxImg} alt="" style={{maxWidth:"95vw",maxHeight:"95vh",objectFit:"contain",borderRadius:8}}/>
        </div>
      )}
      {/* グラフ拡大ライトボックス */}
      {showChartLightbox&&(
        <div onClick={()=>setShowChartLightbox(false)} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.92)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:800,background:"var(--color-background-primary)",borderRadius:12,padding:"1rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <span style={{fontWeight:600,fontSize:15}}>温度カーブ</span>
              <button onClick={()=>setShowChartLightbox(false)} style={{fontSize:14,padding:"4px 12px"}}>✕ 閉じる</button>
            </div>
            <RoastChart points={form.tempPoints} setPoints={setTempPoints} turningPoints={form.turningPoints} customTPs={form.customTPs} powerPoints={form.powerPoints||[]} readOnly={false}/>
          </div>
        </div>
      )}

      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:"1rem",flexWrap:"wrap"}}>
        <button onClick={()=>setView("list")} style={{fontSize:13,padding:"8px 14px"}}>← 一覧</button>
        <h2 style={{fontSize:18,fontWeight:600,margin:0}}>{editId?"プロファイル編集":"新規プロファイル"}</h2>
        {formHistory.length>0&&(
          <button onClick={undoForm} style={{fontSize:13,padding:"6px 12px",border:"1px solid #4a90d9",color:"#4a90d9",borderRadius:7,background:"transparent",cursor:"pointer"}}>↩ 元に戻す</button>
        )}
        {autoSaveMsg&&<span style={{fontSize:12,color:"#2d7a2d",marginLeft:4}}>{autoSaveMsg}</span>}
      </div>

      <Section title="基本情報">
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          {[["焙煎日時","datetime-local","date"],["バッチ数","number","batch"],["豆の名前","text","beanName"],["産地","text","origin"],["投入量 (g)","number","inputWeight"],["完成量 (g)","number","outputWeight"]].map(([label,type,key])=>(
            <div key={key}>
              <label style={{fontSize:12,color:"var(--color-text-secondary)",display:"block",marginBottom:4}}>{label}</label>
              <input type={type} value={form[key]} inputMode={type==="number"?"numeric":undefined} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} style={{fontSize:16}}/>
            </div>
          ))}
        </div>
        {yield_&&<div style={{background:"var(--color-background-secondary)",borderRadius:8,padding:"7px 12px",fontSize:13}}>収率: <strong>{yield_}%</strong></div>}
      </Section>

      <Section title="焙煎環境">
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          <div>
            <label style={{fontSize:12,color:"var(--color-text-secondary)",display:"block",marginBottom:4}}>天気</label>
            <select value={form.weather} onChange={e=>setForm(f=>({...f,weather:e.target.value}))} style={{fontSize:16}}>
              {WEATHER_OPTIONS.map(w=><option key={w}>{w}</option>)}
            </select>
          </div>
          {[["気温 (°C)","temperature"],["湿度 (%)","humidity"]].map(([label,key])=>(
            <div key={key}>
              <label style={{fontSize:12,color:"var(--color-text-secondary)",display:"block",marginBottom:4}}>{label}</label>
              <input type="number" inputMode="numeric" value={form[key]} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} style={{fontSize:16}}/>
            </div>
          ))}
        </div>
      </Section>

      <Section title="タイマー & ターニングポイント">
        <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:16}}>
          <span style={{fontSize:40,fontWeight:600,fontFamily:"monospace",letterSpacing:3,color:"var(--color-text-primary)"}}>{fmt(elapsed)}</span>
          {!timerRunning
            ?<button onClick={startTimer} style={{padding:"12px 24px",background:"#2d7a2d",color:"#fff",border:"none",borderRadius:10,fontSize:16,cursor:"pointer",fontWeight:700}}>START</button>
            :<button onClick={stopTimer}  style={{padding:"12px 24px",background:"#c0392b",color:"#fff",border:"none",borderRadius:10,fontSize:16,cursor:"pointer",fontWeight:700}}>STOP</button>}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
          {form.turningPoints.filter(tp=>!tp.fixed&&!tp.isPower).map(tp=>(
            <div key={tp.id} style={{display:"flex",alignItems:"center",gap:8}}>
              <button onClick={()=>recordTP(tp.id)} disabled={!timerRunning}
                style={{fontSize:14,padding:"10px 20px",background:tp.elapsed!=null?tp.color:"transparent",color:tp.elapsed!=null?"#fff":tp.color,border:`2px solid ${tp.color}`,borderRadius:8,fontWeight:700,minWidth:64}}>
                {tp.label}
              </button>
              <div style={{display:"flex",alignItems:"center",gap:3}}>
                <input type="number" min={0} max={59} inputMode="numeric"
                  placeholder="分"
                  value={tp.elapsed!=null?Math.floor(tp.elapsed/1000/60):""}
                  onChange={e=>{
                    const m=parseInt(e.target.value)||0;
                    const s=tp.elapsed!=null?Math.floor((tp.elapsed/1000)%60):0;
                    const ms=(m*60+s)*1000;
                    setForm(f=>({...f,turningPoints:f.turningPoints.map(t=>
                      t.id===tp.id?{...t,elapsed:ms}:
                      (tp.id==="re"&&t.id==="dev_end")?{...t,elapsed:ms}:t
                    )}));
                  }}
                  style={{width:44,fontSize:14,textAlign:"center",padding:"6px 4px",border:`1px solid ${tp.color}`,borderRadius:7}}/>
                <span style={{color:tp.color,fontWeight:700}}>:</span>
                <input type="number" min={0} max={59} inputMode="numeric"
                  placeholder="秒"
                  value={tp.elapsed!=null?Math.floor((tp.elapsed/1000)%60):""}
                  onChange={e=>{
                    const s=parseInt(e.target.value)||0;
                    const m=tp.elapsed!=null?Math.floor(tp.elapsed/1000/60):0;
                    const ms=(m*60+s)*1000;
                    setForm(f=>({...f,turningPoints:f.turningPoints.map(t=>
                      t.id===tp.id?{...t,elapsed:ms}:
                      (tp.id==="re"&&t.id==="dev_end")?{...t,elapsed:ms}:t
                    )}));
                  }}
                  style={{width:44,fontSize:14,textAlign:"center",padding:"6px 4px",border:`1px solid ${tp.color}`,borderRadius:7}}/>
              </div>
            </div>
          ))}
          {/* 火力ボタン */}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <button onClick={()=>setShowPowerInput(s=>!s)} disabled={!timerRunning}
              style={{fontSize:14,padding:"10px 20px",background:"#e53935",color:"#fff",border:"2px solid #e53935",borderRadius:8,fontWeight:700,minWidth:64}}>
              火力
            </button>
            {showPowerInput&&(
              <div style={{display:"flex",alignItems:"center",gap:8,background:"var(--color-background-secondary)",padding:"8px 10px",borderRadius:8,border:"1px solid var(--color-border-tertiary)"}}>
                <span style={{fontSize:13}}>火力:</span>
                <input type="number" min={0} max={10} inputMode="numeric" value={powerInput} onChange={e=>setPowerInput(Math.min(10,Math.max(0,parseInt(e.target.value)||0)))}
                  style={{width:60,fontSize:16,textAlign:"center"}}/>
                <button onClick={recordPower} disabled={!timerRunning}
                  style={{fontSize:13,padding:"5px 12px",background:"#e53935",color:"#fff",border:"none",borderRadius:6,fontWeight:600}}>記録</button>
              </div>
            )}
          </div>
          {form.customTPs.map(tp=>(
            <button key={tp.id} onClick={()=>recordCTP(tp.id)} disabled={!timerRunning}
              style={{fontSize:14,padding:"10px 20px",background:tp.elapsed!=null?"#607d8b":"transparent",color:tp.elapsed!=null?"#fff":"#607d8b",border:"2px solid #607d8b",borderRadius:8,fontWeight:700}}>
              {tp.label}{tp.elapsed!=null&&<span style={{marginLeft:6,fontSize:11}}>{fmt(tp.elapsed)}</span>}
            </button>
          ))}
        </div>
        {/* 火力ポイント手動追加 */}
        <button onClick={()=>setForm(f=>({...f,powerPoints:[...(f.powerPoints||[]),{id:Date.now(),elapsed:0,value:5}]}))}
          style={{fontSize:12,padding:"5px 12px",marginBottom:8,border:"1px solid #e53935",color:"#e53935",borderRadius:7,background:"transparent"}}>+ 火力ポイントを追加</button>
        {/* 記録済み火力ポイント */}
        {(form.powerPoints||[]).length>0&&(
          <div style={{marginBottom:12}}>
            <div style={{fontSize:12,color:"var(--color-text-secondary)",marginBottom:6}}>記録済み火力:</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {(form.powerPoints||[]).map(pp=>(
                <div key={pp.id} style={{display:"flex",alignItems:"center",gap:6,background:"var(--color-background-secondary)",borderRadius:6,padding:"4px 8px",fontSize:12}}>
                  <span style={{fontSize:11,color:"var(--color-text-secondary)"}}>火力:</span>
                  <input type="number" min={0} max={10} inputMode="numeric" value={pp.value}
                    onChange={e=>setForm(f=>({...f,powerPoints:f.powerPoints.map(p=>p.id===pp.id?{...p,value:Math.min(10,Math.max(0,parseInt(e.target.value)||0))}:p)}))}
                    style={{width:44,fontSize:13,textAlign:"center",padding:"2px 4px",border:"1px solid #e53935",borderRadius:5}}/>
                  <span style={{fontSize:11,color:"var(--color-text-secondary)"}}>時間:</span>
                  <div style={{display:"flex",alignItems:"center",gap:3}}>
                    <input type="number" min={0} max={59} inputMode="numeric" placeholder="分"
                      value={Math.floor(pp.elapsed/1000/60)}
                      onChange={e=>{const m=parseInt(e.target.value)||0;const s=Math.floor((pp.elapsed/1000)%60);setForm(f=>({...f,powerPoints:f.powerPoints.map(p=>p.id===pp.id?{...p,elapsed:(m*60+s)*1000}:p)}));}}
                      style={{width:40,fontSize:13,textAlign:"center",padding:"2px 4px",border:"1px solid #e53935",borderRadius:5}}/>
                    <span style={{color:"#e53935",fontWeight:700}}>:</span>
                    <input type="number" min={0} max={59} inputMode="numeric" placeholder="秒"
                      value={Math.floor((pp.elapsed/1000)%60)}
                      onChange={e=>{const s=parseInt(e.target.value)||0;const m=Math.floor(pp.elapsed/1000/60);setForm(f=>({...f,powerPoints:f.powerPoints.map(p=>p.id===pp.id?{...p,elapsed:(m*60+s)*1000}:p)}));}}
                      style={{width:40,fontSize:13,textAlign:"center",padding:"2px 4px",border:"1px solid #e53935",borderRadius:5}}/>
                  </div>
                  <button onClick={()=>setForm(f=>({...f,powerPoints:f.powerPoints.filter(p=>p.id!==pp.id)}))}
                    style={{fontSize:13,padding:"1px 6px",color:"var(--color-text-danger)",border:"none",background:"transparent",cursor:"pointer"}}>×</button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input type="text" placeholder="カスタムTP名" value={newTPLabel} onChange={e=>setNewTPLabel(e.target.value)} style={{fontSize:16,flex:1}}/>
          <button onClick={addCTP} style={{fontSize:13,padding:"8px 14px",whiteSpace:"nowrap"}}>+ 追加</button>
        </div>
      </Section>

      {/* 焙煎計画 */}
      <Section title="焙煎計画">
        <RoastPlanTable plan={form.roastPlan} setPlan={p=>setForm(f=>({...f,roastPlan:typeof p==="function"?p(f.roastPlan):p}))}/>
      </Section>

      {/* 温度入力テーブル */}
      <Section title="温度入力テーブル">
        <TempTableInput points={form.tempPoints} setPoints={setTempPoints} stopElapsed={devEnd} tpElapsed={getMs("tp")} fcElapsed={getMs("fc")} scElapsed={getMs("sc")}/>
      </Section>

      {/* 温度カーブ */}
      <Section title="温度カーブ">
        <div style={{position:"relative"}}>
          <RoastChart points={form.tempPoints} setPoints={setTempPoints} turningPoints={form.turningPoints} customTPs={form.customTPs} powerPoints={form.powerPoints||[]} readOnly={false}/>
          <button onClick={()=>setShowChartLightbox(true)}
            style={{position:"absolute",top:6,right:6,fontSize:12,padding:"4px 10px",background:"rgba(0,0,0,.45)",color:"#fff",border:"none",borderRadius:6,cursor:"pointer"}}>
            🔍 拡大
          </button>
        </div>
        {totalTime!=null&&(
          <div style={{marginTop:12}}>
            <div style={{fontSize:12,color:"var(--color-text-secondary)",marginBottom:4}}>フェーズ比率 (合計: {fmt(totalTime)})</div>
            <div style={{display:"flex",height:24,borderRadius:6,overflow:"hidden"}}>
              {aPct!=null&&<div style={{width:`${aPct}%`,background:PHASE_COLORS_BAR[0],display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fff",fontWeight:600}}>A {aPct}%</div>}
              {bPct!=null&&<div style={{width:`${bPct}%`,background:PHASE_COLORS_BAR[1],display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fff",fontWeight:600}}>B {bPct}%</div>}
              {cPct!=null&&<div style={{width:`${cPct}%`,background:PHASE_COLORS_BAR[2],display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fff",fontWeight:600}}>C {cPct}%</div>}
            </div>
          </div>
        )}
      </Section>

      <Section title="テイスティング評価">
        <div style={{display:"flex",alignItems:"flex-start",gap:16,flexWrap:"wrap"}}>
          <RadarChart scores={form.scores}/>
          <div style={{flex:1,minWidth:160}}>
            {SCORE_LABELS.map(l=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <span style={{fontSize:13,width:44,color:"var(--color-text-secondary)"}}>{l}</span>
                <input type="range" min={1} max={5} step={1} value={form.scores[l]||3} onChange={e=>setForm(f=>({...f,scores:{...f.scores,[l]:parseInt(e.target.value)}}))} style={{flex:1,height:4}}/>
                <span style={{fontSize:13,width:16,textAlign:"right"}}>{form.scores[l]||3}</span>
              </div>
            ))}
            <div style={{marginTop:8,padding:"10px 14px",border:"2px solid #BA7517",borderRadius:10,background:"rgba(186,117,23,.07)"}}>
              <div style={{fontSize:12,fontWeight:600,color:"#BA7517",marginBottom:8}}>総合評価</div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <input type="range" min={1} max={5} step={1} value={form.scores["総合"]||3} onChange={e=>setForm(f=>({...f,scores:{...f.scores,"総合":parseInt(e.target.value)}}))} style={{flex:1,height:4,accentColor:"#BA7517"}}/>
                <span style={{fontSize:18,fontWeight:700,color:"#BA7517",width:24,textAlign:"right"}}>{form.scores["総合"]||3}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
                {[1,2,3,4,5].map(n=>(
                  <button key={n} onClick={()=>setForm(f=>({...f,scores:{...f.scores,"総合":n}}))}
                    style={{width:36,height:36,borderRadius:"50%",border:"2px solid #BA7517",background:(form.scores["総合"]||3)>=n?"#BA7517":"transparent",color:(form.scores["総合"]||3)>=n?"#fff":"#BA7517",fontSize:14,fontWeight:700,cursor:"pointer"}}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section title="メモ">
        <textarea value={form.memo} onChange={e=>setForm(f=>({...f,memo:e.target.value}))} rows={4}
          style={{width:"100%",fontSize:16,lineHeight:1.7}} placeholder={"焙煎の気づき、次回へのメモなど...\n（Enterキーで改行できます）"}/>
        <div style={{marginTop:10}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <button onClick={()=>imageInputRef.current?.click()} style={{fontSize:14,padding:"8px 16px"}}>📎 画像を追加</button>
            <span style={{fontSize:11,color:"var(--color-text-tertiary)"}}>JPG / PNG / GIF</span>
          </div>
          <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} style={{display:"none"}}/>
          {(form.memoImages||[]).length>0&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:10,marginTop:8}}>
              {(form.memoImages||[]).map(img=>(
                <div key={img.id} style={{position:"relative",border:"1px solid var(--color-border-tertiary)",borderRadius:8,overflow:"hidden"}}>
                  <img src={img.src} alt={img.name} onClick={()=>setLightboxImg(img.src)}
                    style={{width:90,height:90,objectFit:"cover",display:"block",cursor:"pointer"}}/>
                  <button onClick={()=>removeImage(img.id)}
                    style={{position:"absolute",top:3,right:3,width:22,height:22,borderRadius:"50%",background:"rgba(0,0,0,.6)",color:"#fff",border:"none",cursor:"pointer",fontSize:14,lineHeight:"22px",textAlign:"center",padding:0}}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      <button onClick={saveProfile}
        style={{width:"100%",padding:"14px",background:"#BA7517",color:"#fff",border:"none",borderRadius:10,fontSize:17,cursor:"pointer",fontWeight:700,marginBottom:32}}>
        {editId?"更新する":"保存する"}
      </button>
    </div>
  );
}